import moment from 'moment';
import type { AccountData } from '../../api/models/AccountData';
import type { EventScheduleData } from '../../api/models/EventScheduleData';
import type { OrderPageData } from '../../api/models/OrderPageData';
import type { PassOrderPageAvailabilityOut } from '../../api/models/PassOrderPageAvailabilityOut';
import type { PassOrderPageData } from '../../api/models/PassOrderPageData';
import type { PassScheduleSelectionDTO } from '../../api/models/PassScheduleSelectionDTO';
import type { WorkingDaysData } from '../../api/models/WorkingDaysData';

export type Weekday = keyof EventScheduleData & keyof WorkingDaysData;

export const WEEKDAYS: Weekday[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export type WeeklyOptions = Record<string, number[]>;

export const findOrderPageById = (account: AccountData, orderPageId: string): OrderPageData | undefined => {
  for (const exp of account.experiences || []) {
    const op = exp.order_pages?.find((p) => p.id === orderPageId);
    if (op) return op;
  }
  return undefined;
};

const uniqueSorted = (values: number[]): number[] => Array.from(new Set(values)).sort((a, b) => a - b);

export const buildWeeklyOptions = (page: OrderPageData): WeeklyOptions => {
  const result: WeeklyOptions = {};
  const eventSchedule = page.event_schedule;
  const workingDays = page.working_days;

  for (const weekday of WEEKDAYS) {
    const fromEvent = eventSchedule?.[weekday]?.map((slot) => slot.start_time) ?? [];
    const fromWorking = workingDays?.[weekday]?.start_seconds ?? [];
    const merged = uniqueSorted([...fromEvent, ...fromWorking]);
    if (merged.length > 0) {
      result[weekday] = merged;
    }
  }
  return result;
};

export type ResolvedRedeemablePage = {
  orderPageId: string;
  page: OrderPageData;
  weeklyOptions: WeeklyOptions;
};

export const resolveRedeemablePages = (
  passOrderPage: PassOrderPageData,
  account: AccountData,
): ResolvedRedeemablePage[] => {
  return (passOrderPage.redeemable_on || [])
    .map(({ order_page_id }) => {
      const page = findOrderPageById(account, order_page_id);
      if (!page) return null;
      const weeklyOptions = buildWeeklyOptions(page);
      if (Object.keys(weeklyOptions).length === 0) return null;
      return { orderPageId: order_page_id, page, weeklyOptions };
    })
    .filter((entry): entry is ResolvedRedeemablePage => entry !== null);
};

export const isSelected = (
  selections: PassScheduleSelectionDTO[],
  orderPageId: string,
  weekday: string,
  startTime: number,
): boolean =>
  selections.some((s) => s.order_page_id === orderPageId && s.weekday === weekday && s.start_time === startTime);

export const isSlotDisabled = (
  availability: PassOrderPageAvailabilityOut[] | undefined,
  orderPageId: string,
  weekday: string,
  startTime: number,
): boolean => {
  if (!availability) return false;
  const page = availability.find((a) => a.order_page_id === orderPageId);
  const day = page?.availability.find((w) => w.weekday === weekday);
  const slot = day?.slots.find((s) => s.startTime === startTime);
  return slot?.isDisabled === true;
};

/**
 * Drops the slots that have filled up since the availability grid was last loaded. Used after the
 * server rejects a purchase for capacity, so the customer is pushed to re-pick rather than
 * resubmitting the same full slot.
 */
export const dropUnavailableSelections = (
  selections: PassScheduleSelectionDTO[],
  availability: PassOrderPageAvailabilityOut[] | undefined,
): PassScheduleSelectionDTO[] =>
  selections.filter((s) => !isSlotDisabled(availability, s.order_page_id, s.weekday, s.start_time));

export const toggleSelection = (
  selections: PassScheduleSelectionDTO[],
  orderPageId: string,
  weekday: string,
  startTime: number,
): PassScheduleSelectionDTO[] => {
  if (isSelected(selections, orderPageId, weekday, startTime)) {
    return selections.filter(
      (s) => !(s.order_page_id === orderPageId && s.weekday === weekday && s.start_time === startTime),
    );
  }
  return [...selections, { order_page_id: orderPageId, weekday, start_time: startTime }];
};

export const WEEKDAY_I18N_KEYS: Record<Weekday, string> = {
  sunday: 'pass_schedule.weekday.sunday',
  monday: 'pass_schedule.weekday.monday',
  tuesday: 'pass_schedule.weekday.tuesday',
  wednesday: 'pass_schedule.weekday.wednesday',
  thursday: 'pass_schedule.weekday.thursday',
  friday: 'pass_schedule.weekday.friday',
  saturday: 'pass_schedule.weekday.saturday',
};

export const weekdayLabel = (t: (key: string) => string, weekday: string): string =>
  t(WEEKDAY_I18N_KEYS[weekday as Weekday] || weekday);

export const sortScheduleSelections = (selections: PassScheduleSelectionDTO[]): PassScheduleSelectionDTO[] =>
  [...selections].sort((a, b) => {
    const wa = WEEKDAYS.indexOf(a.weekday as Weekday);
    const wb = WEEKDAYS.indexOf(b.weekday as Weekday);
    if (wa !== wb) return wa - wb;
    return a.start_time - b.start_time;
  });

export const formatSlotLabel = (
  t: (key: string) => string,
  weekday: string,
  startTime: number,
  timeFormat: string,
): string => `${weekdayLabel(t, weekday)} · ${moment.utc(startTime * 1000).format(timeFormat)}`;

export type ScheduleGroup = {
  opId: string;
  pageLabel: string | undefined;
  sorted: PassScheduleSelectionDTO[];
};

/**
 * Groups a pass's weekly slots by the activity they belong to, so a schedule spanning several
 * activities can be rendered under its own heading.
 */
export const groupSelectionsByOrderPage = (
  selections: PassScheduleSelectionDTO[] | undefined,
  account: AccountData,
  language: string,
): ScheduleGroup[] => {
  const byPage = new Map<string, PassScheduleSelectionDTO[]>();
  for (const sel of selections || []) {
    const list = byPage.get(sel.order_page_id) || [];
    list.push(sel);
    byPage.set(sel.order_page_id, list);
  }
  return Array.from(byPage.entries()).map(([opId, pageSelections]) => {
    const page = findOrderPageById(account, opId);
    const pageLabel = (page?.label as Record<string, string> | undefined)?.[language] || page?.label?.en;
    return { opId, pageLabel, sorted: sortScheduleSelections(pageSelections) };
  });
};
